import { useSelector } from 'react-redux';

import {
  Icon,
  Input,
  Label,
  Modal,
  PaymentCollection,
  Tooltip
} from '@components';
import { FEATURE_FLAGS } from '@constants/feature';
import { OrgStatus } from '@constants/generic';
import {
  getAdvancedByTermFrequencyPlaceholder,
  getPaymentCollectionType
} from '@features/group-management/helper';
import { getDueDateTooltipText } from '@features/group-management/helpers/createGroup';
import { PaymentCollectionArg, PaymentCollectionComponentType } from '@types';

import theme from '../../../../../../themes/colors';

interface GroupDueDateSelectionProps {
  formData: any;
  isDueDateDisabled: boolean;
  isEditGroup: boolean;
  isPaymentCollectionModalOpen: boolean;
  setIsPaymentCollectionModalOpen: (value: boolean) => void;
  dueDateValue: string;
  onDueDateChange: (value: PaymentCollectionArg) => void;
  paymentCollectionComponentType: PaymentCollectionComponentType;
  byTermAdvancedDefaultLabel?: string;
}

const GroupDueDateSelection = ({
  formData,
  isDueDateDisabled,
  isEditGroup,
  isPaymentCollectionModalOpen,
  setIsPaymentCollectionModalOpen,
  dueDateValue,
  onDueDateChange,
  paymentCollectionComponentType,
  byTermAdvancedDefaultLabel
}: GroupDueDateSelectionProps) => {
  const { currentOrg, orgFeatureFlags } = useSelector(
    (state: any) => state.user
  );

  const isInstantOrderGenerationEnabled = Boolean(
    orgFeatureFlags?.includes(FEATURE_FLAGS.GROUP_INSTANT_ORDER_GENERATION)
  );

  const isOrgLite =
    currentOrg?.status === OrgStatus.KYC_PENDING ||
    currentOrg?.status === OrgStatus.KYC_SUBMITTED;

  const dueDateTooltipText = getDueDateTooltipText(formData, isEditGroup);

  return (
    <>
      <div className="flex items-center gap-2 text-sm font-semibold text-grey">
        <Label
          label="When do you want to collect fees?"
          className="!mb-2 !text-content"
          isRequired
        />
      </div>
      <div data-tooltip-id="due-date-tooltip">
        <Input
          readOnly
          leftIconName="calendar"
          value={dueDateValue}
          onClick={() => {
            setIsPaymentCollectionModalOpen(true);
          }}
          placeholder="Select payment collection day"
          disabled={isDueDateDisabled}
          inputStyle="!bg-theme"
        />
        {formData.activationDate && !formData.isCustomTerm && (
          <div className="mt-1 flex items-center gap-1 text-sm text-grey60">
            <Icon name="alert-circle" size="16" stroke={theme.subText} />
            <div>
              {`Payment ${isOrgLite ? 'request' : 'link'} will be generated ${
                formData.isImmediatePayment ? '' : 'on'
              }`}{' '}
              {dueDateTooltipText}
            </div>
          </div>
        )}
      </div>
      {isEditGroup &&
        (formData.isImmediatePayment || formData.isCustomTerm) && (
          <Tooltip
            id="due-date-tooltip"
            type="text"
            tooltipText={`Collection frequency is set to ${
              formData.isImmediatePayment
                ? 'Once: Immediately'
                : getAdvancedByTermFrequencyPlaceholder(
                    byTermAdvancedDefaultLabel
                  )
            } and cannot be modified.`}
            className="max-w-[300px]"
            place="top-end"
          />
        )}
      {isPaymentCollectionModalOpen && (
        <Modal
          isOpen={isPaymentCollectionModalOpen}
          onCancel={() => setIsPaymentCollectionModalOpen(false)}
          isCloseIconRequired
        >
          <PaymentCollection
            type={
              isEditGroup
                ? paymentCollectionComponentType
                : PaymentCollectionComponentType.DEFAULT
            }
            isOpen={isPaymentCollectionModalOpen}
            confirmButtonLabel="Apply"
            confirmButtonStyle="text-primary"
            handleConfirm={onDueDateChange}
            cancelButtonLabel="Cancel"
            handleCancel={() => {
              setIsPaymentCollectionModalOpen(false);
            }}
            selectedDate={formData?.activationDate}
            selectedtype={
              isEditGroup
                ? getPaymentCollectionType(
                    formData.paymentCollectionType,
                    formData.intervalFrequency
                  )
                : formData?.paymentCollectionType
            }
            selectedFrequency={formData?.intervalFrequency}
            {...(isEditGroup && { nextDueDate: formData?.activationDate })}
            isPaymentLinkSent={formData.isPaymentLinkSent}
            isImmediatePayment={formData.isImmediatePayment}
            isCustomTerm={formData.isCustomTerm}
            isEditGroup={isEditGroup}
            showImmediateInfo={isInstantOrderGenerationEnabled}
          />
        </Modal>
      )}
    </>
  );
};

export default GroupDueDateSelection;
